import { BubbleMenu, type Editor } from "@tiptap/react";
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  AlignLeft,
  AlignCenter,
  AlignRight,
  List,
  Heading1,
  Heading2,
} from "lucide-react";
import { cn } from "../../lib/utils";

interface FloatingToolbarProps {
  editor: Editor;
}

export function FloatingToolbar({ editor }: FloatingToolbarProps) {
  return (
    <BubbleMenu
      editor={editor}
      tippyOptions={{ duration: 100 }}
      className="flex items-center gap-1 p-1.5 bg-background/95 border border-border/50 rounded-md shadow-lg backdrop-blur-sm"
    >
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={cn(
          "p-1.5 rounded hover:bg-accent hover:text-accent-foreground transition-colors",
          editor.isActive('bold') && "bg-accent text-accent-foreground"
        )}
        title="Gras"
      >
        <Bold size={16} />
      </button>
      
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className={cn(
          "p-1.5 rounded hover:bg-accent hover:text-accent-foreground transition-colors",
          editor.isActive('italic') && "bg-accent text-accent-foreground"
        )}
        title="Italique"
      >
        <Italic size={16} />
      </button>
      
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        className={cn(
          "p-1.5 rounded hover:bg-accent hover:text-accent-foreground transition-colors",
          editor.isActive('underline') && "bg-accent text-accent-foreground"
        )}
        title="Souligné"
      >
        <UnderlineIcon size={16} />
      </button>
      
      <div className="w-px h-4 bg-border/30 mx-1" />
      
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        className={cn(
          "p-1.5 rounded hover:bg-accent hover:text-accent-foreground transition-colors",
          editor.isActive('heading', { level: 1 }) && "bg-accent text-accent-foreground"
        )}
        title="Titre 1"
      >
        <Heading1 size={16} />
      </button>
      
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        className={cn(
          "p-1.5 rounded hover:bg-accent hover:text-accent-foreground transition-colors",
          editor.isActive('heading', { level: 2 }) && "bg-accent text-accent-foreground"
        )}
        title="Titre 2"
      >
        <Heading2 size={16} />
      </button>
      
      <div className="w-px h-4 bg-border/30 mx-1" />
      
      <button
        type="button"
        onClick={() => editor.chain().focus().setTextAlign('left').run()}
        className={cn(
          "p-1.5 rounded hover:bg-accent hover:text-accent-foreground transition-colors",
          editor.isActive({ textAlign: 'left' }) && "bg-accent text-accent-foreground"
        )}
        title="Aligner à gauche"
      >
        <AlignLeft size={16} />
      </button>
      
      <button
        type="button"
        onClick={() => editor.chain().focus().setTextAlign('center').run()}
        className={cn(
          "p-1.5 rounded hover:bg-accent hover:text-accent-foreground transition-colors",
          editor.isActive({ textAlign: 'center' }) && "bg-accent text-accent-foreground"
        )}
        title="Centrer"
      >
        <AlignCenter size={16} />
      </button>
      
      <button
        type="button"
        onClick={() => editor.chain().focus().setTextAlign('right').run()}
        className={cn(
          "p-1.5 rounded hover:bg-accent hover:text-accent-foreground transition-colors",
          editor.isActive({ textAlign: 'right' }) && "bg-accent text-accent-foreground"
        )}
        title="Aligner à droite"
      >
        <AlignRight size={16} />
      </button>
      
      <div className="w-px h-4 bg-border/30 mx-1" />
      
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={cn(
          "p-1.5 rounded hover:bg-accent hover:text-accent-foreground transition-colors",
          editor.isActive('bulletList') && "bg-accent text-accent-foreground"
        )}
        title="Liste à puces"
      >
        <List size={16} />
      </button>
    </BubbleMenu>
  );
}